import React, { useMemo, useState, useEffect } from 'react';
import { useSalesReport } from '../../hooks/useSalesReport';
import { useInventory } from '../../hooks/useInventory';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../lib/utils';
import DashboardHeader from './DashboardHeader';
import DatePresetButtons, { getPresetRange, DEFAULT_PRESET } from '../common/DatePresetButtons';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { es } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, ShoppingBag, DollarSign, List, BarChart3, RefreshCw, Calendar, Info, MapPin } from 'lucide-react';
import { startOfMonth, endOfMonth, startOfToday, format } from 'date-fns';

export default function KPIAnalyticsScreen() {
    const navigate = useNavigate();

    // Date Range State
    const [dateRange, setDateRange] = useState(() => getPresetRange(DEFAULT_PRESET));
    const [datePreset, setDatePreset] = useState(DEFAULT_PRESET);
    const [selectedBranch, setSelectedBranch] = useState('all');

    // Hooks
    const { report, loading, getSalesReport, error } = useSalesReport();
    const { inventory, loading: inventoryLoading, fetchInventory } = useInventory();

    // Load sales when date changes
    useEffect(() => {
        if (dateRange.from && dateRange.to) {
            const start = format(dateRange.from, 'yyyy-MM-dd');
            const end = format(dateRange.to, 'yyyy-MM-dd');
            getSalesReport(start, end);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange.from, dateRange.to]);

    // Load inventory on mount
    useEffect(() => {
        fetchInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle date preset changes
    const handleDatePresetChange = (preset, range) => {
        setDatePreset(preset);
        setDateRange(range);
    };

    // Handle Retry
    const handleRetry = () => {
        if (dateRange.from && dateRange.to) {
            const start = format(dateRange.from, 'yyyy-MM-dd');
            const end = format(dateRange.to, 'yyyy-MM-dd');
            getSalesReport(start, end);
        }
    };

    // --- Branch Management ---
    const availableBranches = useMemo(() => {
        if (!report || !report.orders) return [];
        const branches = new Set();
        report.orders.forEach(order => {
            if (order.branch) branches.add(order.branch);
        });
        return Array.from(branches).sort();
    }, [report]);

    const filteredOrders = useMemo(() => {
        if (!report || !report.orders) return [];
        if (selectedBranch === 'all') return report.orders;
        return report.orders.filter(order => order.branch === selectedBranch);
    }, [report, selectedBranch]);

    // --- Merge Products with Inventory ---
    const mergedProducts = useMemo(() => {
        if (!filteredOrders || filteredOrders.length === 0) return [];

        console.log('[KPIAnalytics] Merging products with inventory...');

        // Build inventory indexes for fast lookup
        const inventoryMapById = new Map();
        const inventoryMapBySku = new Map();
        const inventoryMapByName = new Map();

        inventory.forEach(inv => {
            inventoryMapById.set(String(inv.id), inv);
            if (inv.sku) {
                inventoryMapBySku.set(inv.sku.toLowerCase().trim(), inv);
            }
            if (inv.name) {
                inventoryMapByName.set(inv.name.toLowerCase().trim(), inv);
            }
        });

        console.log(`[KPIAnalytics] Built inventory indexes: ${inventoryMapById.size} by ID, ${inventoryMapBySku.size} by SKU, ${inventoryMapByName.size} by name`);

        // Process all items from filtered orders
        const mergedItems = [];

        filteredOrders.forEach(order => {
            const items = order.items || order.products || [];
            items.forEach(item => {
                const productId = String(item.id);
                const sku = item.sku || item.codigoBarra || '';
                const name = item.name || item.nombre || '';

                // Try matching: ID → SKU → Name
                let inv = inventoryMapById.get(productId);
                if (!inv && sku) {
                    inv = inventoryMapBySku.get(sku.toLowerCase().trim());
                }
                if (!inv && name) {
                    inv = inventoryMapByName.get(name.toLowerCase().trim());
                }

                // Build merged item with inventory data if found
                mergedItems.push({
                    id: productId,
                    name: inv?.name || name,
                    brand: inv?.brand || item.brandName || "Sin Marca",
                    category: inv?.category || item.categoryName || "General",
                    sku: inv?.sku || sku,
                    quantity: Number(item.quantity) || 0,
                    totalSales: Number(item.totalSales) || 0,
                    matchedBy: inv ? (inventoryMapById.get(productId) ? 'id' : (inventoryMapBySku.get(sku?.toLowerCase().trim()) ? 'sku' : 'name')) : 'none'
                });
            });
        });

        console.log(`[KPIAnalytics] Merged ${mergedItems.length} items from ${filteredOrders.length} orders`);

        return mergedItems;
    }, [filteredOrders, inventory]);

    // --- KPIs Calculation ---
    const indicators = useMemo(() => {
        if (mergedProducts.length === 0) return {
            units: 0,
            netSales: 0,
            avgPrice: 0,
            ordersCount: 0,
            upt: 0,
            avgTicket: 0
        };

        const totalOrders = filteredOrders.length || 0;
        let totalUnits = 0;
        let totalNetSales = 0;

        mergedProducts.forEach(item => {
            totalUnits += item.quantity;
            totalNetSales += item.totalSales;
        });

        const avgPrice = totalUnits > 0 ? totalNetSales / totalUnits : 0;
        const avgTicket = totalOrders > 0 ? totalNetSales / totalOrders : 0;
        const upt = totalOrders > 0 ? totalUnits / totalOrders : 0;

        return {
            units: totalUnits,
            netSales: totalNetSales,
            avgPrice,
            ordersCount: totalOrders,
            upt,
            avgTicket
        };
    }, [mergedProducts, filteredOrders]);

    // --- Brand Analysis ---
    const brandData = useMemo(() => {
        if (mergedProducts.length === 0) return [];
        const map = new Map();

        mergedProducts.forEach(item => {
            const brand = item.brand || "Sin Marca";
            if (!map.has(brand)) map.set(brand, { name: brand, units: 0, netSales: 0 });
            const entry = map.get(brand);
            entry.units += item.quantity;
            entry.netSales += item.totalSales;
        });

        return Array.from(map.values())
            .map(b => ({
                ...b,
                share: indicators.netSales > 0 ? (b.netSales / indicators.netSales) * 100 : 0,
                avgPrice: b.units > 0 ? b.netSales / b.units : 0
            }))
            .sort((a, b) => b.netSales - a.netSales);
    }, [mergedProducts, indicators.netSales]);

    // --- Line Analysis ---
    const lineData = useMemo(() => {
        if (mergedProducts.length === 0) return [];
        const map = new Map();

        mergedProducts.forEach(item => {
            const line = item.category || "General";
            if (!map.has(line)) map.set(line, { name: line, units: 0, netSales: 0 });
            const entry = map.get(line);
            entry.units += item.quantity;
            entry.netSales += item.totalSales;
        });

        return Array.from(map.values())
            .map(l => ({
                ...l,
                share: indicators.netSales > 0 ? (l.netSales / indicators.netSales) * 100 : 0,
                avgPrice: l.units > 0 ? l.netSales / l.units : 0
            }))
            .sort((a, b) => b.netSales - a.netSales);
    }, [mergedProducts, indicators.netSales]);

    return (
        <div className="min-h-screen bg-gray-950 text-white px-4 py-5 md:p-8 space-y-4 md:space-y-6">
            <DashboardHeader
                activeTab="kpi"
                title="Indicadores Comerciales"
                subtitle="KPIs, marcas y líneas de producto"
            />

            {/* Date Selector */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-wrap items-center gap-2 bg-slate-900/50 p-1.5 rounded-lg border border-white/10">
                    <DatePresetButtons value={datePreset} onChange={handleDatePresetChange} disabled={loading} className="border-0 bg-transparent p-0" />

                    <div className="h-4 w-px bg-white/10 mx-2 hidden md:block" />

                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    "h-9 border-white/10 bg-transparent text-slate-400 hover:text-white hover:bg-white/5 justify-start text-left font-normal px-4",
                                    !dateRange && "text-muted-foreground"
                                )}
                            >
                                <Calendar className="mr-2 h-4 w-4" />
                                {dateRange?.from ? (
                                    dateRange.to ? (
                                        <>
                                            {format(dateRange.from, "dd MMM", { locale: es })} -{" "}
                                            {format(dateRange.to, "dd MMM", { locale: es })}
                                        </>
                                    ) : (
                                        format(dateRange.from, "dd MMM", { locale: es })
                                    )
                                ) : (
                                    <span>Seleccionar fecha</span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-slate-950 border-white/10" align="start">
                            <CalendarComponent
                                initialFocus
                                mode="range"
                                defaultMonth={dateRange?.from}
                                selected={dateRange}
                                onSelect={(range) => {
                                    if (range) {
                                        setDateRange(range);
                                        setDatePreset('custom');
                                    }
                                }}
                                numberOfMonths={2}
                                locale={es}
                                className="bg-slate-950 text-white"
                            />
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1 bg-slate-900/50 p-1.5 rounded-lg border border-white/10 lg:w-auto w-full overflow-x-auto">
                        <div className="flex items-center px-2 text-slate-400 border-r border-white/10 mr-1 shrink-0">
                            <MapPin className="w-4 h-4" />
                        </div>
                        <Button
                            variant={selectedBranch === 'all' ? 'default' : 'ghost'}
                            onClick={() => setSelectedBranch('all')}
                            className={cn(
                                "h-8 px-3 text-xs font-medium rounded-md transition-all",
                                selectedBranch === 'all'
                                    ? "bg-blue-600 text-white shadow-sm shadow-blue-500/20"
                                    : "text-slate-400 hover:text-white hover:bg-white/5"
                            )}
                        >
                            Todas
                        </Button>
                        {availableBranches.map(branch => (
                            <Button
                                key={branch}
                                variant={selectedBranch === branch ? 'default' : 'ghost'}
                                onClick={() => setSelectedBranch(branch)}
                                className={cn(
                                    "h-8 px-3 text-xs font-medium rounded-md whitespace-nowrap transition-all",
                                    selectedBranch === branch
                                        ? "bg-blue-600 text-white shadow-sm shadow-blue-500/20"
                                        : "text-slate-400 hover:text-white hover:bg-white/5"
                                )}
                            >
                                {branch}
                            </Button>
                        ))}
                    </div>

                    <div className="flex items-center gap-3">
                        {inventory.length > 0 && (
                            <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20 py-1.5 px-3">
                                <Info className="w-3.5 h-3.5 mr-2" />
                                Inventario: {inventory.length} SKUs
                            </Badge>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => fetchInventory(true)}
                            disabled={inventoryLoading}
                            className="gap-2 h-9 px-4 bg-white/5 border-white/10 hover:bg-white/10 hover:text-white text-slate-300"
                        >
                            {inventoryLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="hidden sm:inline">Actualizando...</span>
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="w-4 h-4" />
                                    <span className="hidden sm:inline">Recargar Datos</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-md flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <p className="text-sm font-medium">{error.toString()}</p>
                    <Button variant="ghost" size="sm" className="ml-auto h-6 text-red-400 hover:text-red-300" onClick={handleRetry}>Reintentar</Button>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-white/60" />
                </div>
            ) : (
                <>
                    {/* Global KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <KPICard title="Unidades de Venta" value={indicators.units} icon={ShoppingBag} color="text-blue-400" border="border-blue-500/20" bg="bg-blue-500/10" />
                        <KPICard title="Venta Neta" value={formatCurrency(indicators.netSales)} icon={DollarSign} color="text-green-400" border="border-green-500/20" bg="bg-green-500/10" />
                        <KPICard title="Precio Promedio" value={formatCurrency(indicators.avgPrice)} icon={TrendingUp} color="text-purple-400" border="border-purple-500/20" bg="bg-purple-500/10" />
                        <KPICard title="No. Pedidos" value={indicators.ordersCount} icon={List} color="text-orange-400" border="border-orange-500/20" bg="bg-orange-500/10" />
                        <KPICard title="UPT" value={indicators.upt.toFixed(2)} icon={BarChart3} color="text-cyan-400" border="border-cyan-500/20" bg="bg-cyan-500/10" />
                        <KPICard title="Factura Promedio" value={formatCurrency(indicators.avgTicket)} icon={DollarSign} color="text-yellow-400" border="border-yellow-500/20" bg="bg-yellow-500/10" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Sales by Brand */}
                        <Card className="bg-transparent border-white/10">
                            <CardHeader>
                                <CardTitle>Ventas por Marca</CardTitle>
                                <CardDescription className="text-white/60">
                                    {inventory.length > 0
                                        ? `Vinculadas con inventario (${inventory.length} productos)`
                                        : 'Sin vinculación de inventario - carga inventario para ver marcas reales'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-0">
                                <DataTable data={brandData} label="Marca" />
                            </CardContent>
                        </Card>

                        {/* Sales by Line */}
                        <Card className="bg-transparent border-white/10">
                            <CardHeader>
                                <CardTitle>Ventas por Línea de Producto</CardTitle>
                                <CardDescription className="text-white/60">
                                    Categorías y líneas de productos
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-0">
                                <DataTable data={lineData} label="Línea" />
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}

function KPICard({ title, value, icon, color, border, bg }) {
    const Icon = icon;
    return (
        <Card className={`${bg} ${border} backdrop-blur shadow-lg shadow-black/5`}>
            <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
                <div className={cn("p-3 rounded-full bg-white/5", color)}>
                    <Icon className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{title}</p>
                    <p className={`text-2xl font-bold tracking-tight ${color}`}>{value}</p>
                </div>
            </CardContent>
        </Card>
    );
}

function DataTable({ data, label }) {
    if (data.length === 0) return <div className="p-8 text-center text-white/50">No hay datos</div>;

    return (
        <div className="overflow-x-auto">
            <Table>
                <TableHeader className="bg-white/5 hover:bg-white/5">
                    <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/70">{label}</TableHead>
                        <TableHead className="text-right text-white/70">Unidades</TableHead>
                        <TableHead className="text-right text-white/70">Venta Neta</TableHead>
                        <TableHead className="text-right text-white/70">% de Venta</TableHead>
                        <TableHead className="text-right text-white/70">Promedio</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className="text-white">
                    {data.map((row, i) => (
                        <TableRow key={i} className="border-white/10 hover:bg-white/5">
                            <TableCell className="font-medium text-xs">{row.name}</TableCell>
                            <TableCell className="text-right text-xs">{row.units}</TableCell>
                            <TableCell className="text-right font-bold text-xs text-green-400">{formatCurrency(row.netSales)}</TableCell>
                            <TableCell className="text-right text-xs">{row.share.toFixed(1)}%</TableCell>
                            <TableCell className="text-right text-xs">{formatCurrency(row.avgPrice)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
